import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CheckCircle, XCircle } from 'lucide-react';
import { BaseLayout, FileInput } from '../../../components';
import { Card, CardContent, Button, Label, Input } from '@/components/ui';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Spinner } from '@/components';
import { TOAST_TYPES } from '@/constants/common.constants';
import { useToast } from '@/contexts/toast-context';
import { isDriverIdChannel } from '@/utils/account-channel';
import { bulkProvisionUsers } from '@/api/user.api';
import { getAccounts } from '@/api/account.api';
import { SelectDropdown } from '@/components/utility/select-dropdown/select-dropdown';
import type {
  BulkProvisionResponse,
  BulkProvisionResult,
} from '@/types/user.types';
import type { AccountListItem } from '@/types/accounts.types';
import './bulk-provision-style.scss';

interface FormValues {
  accountUuid: string;
  file: File | null;
  sendInvitation: boolean;
}

const validationSchema = Yup.object({
  accountUuid: Yup.string().required('Please select an account'),
  file: Yup.mixed<File>().required('Please select a CSV file'),
});

export const Route = createFileRoute('/organizations/bulk-provision/')({
  component: BulkProvisionUsersPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      account: (search.account as string) || undefined,
    };
  },
});

function BulkProvisionUsersPage() {
  const { account } = Route.useSearch();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [result, setResult] = useState<BulkProvisionResponse | null>(null);
  const [accounts, setAccounts] = useState<AccountListItem[]>([]);
  const [selectedAccountUuid, setSelectedAccountUuid] = useState(account || '');

  // Driver ID accounts invert the requirements: the Driver ID becomes mandatory and the
  // email optional, for drivers only. The panel must say so, or admins follow on-screen
  // instructions straight into a rejected upload.
  const selectedChannel = accounts.find(
    (a) => a.uuid === selectedAccountUuid,
  )?.driver_login_channel;
  const isDriverIdAccount = isDriverIdChannel(selectedChannel);

  useEffect(() => {
    const fetchAccountsData = async () => {
      try {
        const response = await getAccounts();
        setAccounts(response || []);
      } catch (error) {
        addToast('Failed to fetch accounts.', {
          type: TOAST_TYPES.ERROR,
        });
      }
    };
    fetchAccountsData();
  }, []);

  const handleSubmit = async (values: FormValues) => {
    try {
      setIsUploading(true);
      setParseErrors([]);
      setResult(null);

      const response = await bulkProvisionUsers(
        values.file!,
        values.accountUuid,
        values.sendInvitation,
      );

      setResult(response);

      if (response.successCount > 0) {
        addToast(`Successfully provisioned ${response.successCount} user(s)`, {
          type:
            response.failureCount > 0
              ? TOAST_TYPES.WARNING
              : TOAST_TYPES.SUCCESS,
          title:
            response.failureCount > 0
              ? 'Bulk Provision Completed with Warnings'
              : 'Bulk Provision Completed',
        });
      } else {
        addToast('All users failed to provision', {
          type: TOAST_TYPES.ERROR,
          title: 'Bulk Provision Failed',
        });
      }
    } catch (error: any) {
      if (Array.isArray(error?.errors) && error.errors.length > 0) {
        setParseErrors(error.errors);
      }
      addToast(error?.message || 'Upload failed', {
        type: TOAST_TYPES.ERROR,
        title: 'Upload Error',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    // The asterisks mark the required columns, and the Driver ID channel inverts two of
    // them: drivers are identified by their Driver ID and need no email. Matches the
    // guide above, so the template and the on-screen instructions never disagree.
    // An asterisk marks what a driver row needs — the Driver ID channel makes the Driver
    // ID required and the email optional. Manager Email carries one on both channels
    // because every driver needs a manager, whatever the channel. Non-driver rows read
    // differently (they always need an email and never a Driver ID); the guide above
    // states the per-role rules, and the server enforces them per row regardless of what
    // the header says.
    const headerRow = isDriverIdAccount
      ? 'First Name *,Last Name *,Title *,Email,Phone,Role *,Driver ID *,Manager Email *'
      : 'First Name *,Last Name *,Title *,Email *,Phone,Role *,Driver ID,Manager Email *';

    // Only the driver row changes: on the Driver ID channel a driver has no email, so the
    // sample leaves it blank rather than inviting the admin to copy a shape the headers
    // just said was optional. Managers and executives still sign in by email either way.
    const driverRow = isDriverIdAccount
      ? 'Sarah,Johnson,Fleet Driver,,555-234-5678,driver,DRV-001,john.smith@example.com'
      : 'Sarah,Johnson,Fleet Driver,sarah.johnson@example.com,555-234-5678,driver,DRV-001,john.smith@example.com';

    const templateContent = [
      headerRow,
      'John,Smith,Operations Manager,john.smith@example.com,555-123-4567,manager,,',
      driverRow,
      'Michael,Brown,Executive Director,michael.brown@example.com,555-345-6789,executive,,',
    ].join('\n');

    const blob = new Blob([templateContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'bulk-provision-users-template.csv';
    link.click();

    URL.revokeObjectURL(url);
  };

  const failures = result?.results.filter((r) => !r.success) ?? [];

  return (
    <BaseLayout
      header={{
        headerText: 'Bulk Provision',
      }}
      breadcrumbs={{
        showBreadcrumbs: true,
        breadcrumbText: ['Organization', 'Bulk Provision'],
      }}
    >
      <Card>
        <CardContent>
          <div className="bulk-provision-guide">
            <p className="bulk-provision-guide__title">
              CSV Upload Instructions
            </p>
            <div className="bulk-provision-guide__row">
              <span className="bulk-provision-guide__label">Info</span>
              <span>
                Upload a CSV to provision up to 500 users at once.{' '}
                <Button
                  variant="link-primary"
                  size="medium"
                  className="p-0 bulk-provision-guide__download-btn"
                  onClick={handleDownloadTemplate}
                >
                  Download template
                </Button>
              </span>
            </div>
            <div className="bulk-provision-guide__row">
              <span className="bulk-provision-guide__label">Required</span>
              <span>
                First Name{' '}
                <span className="bulk-provision-guide__hint">(max 100)</span>,{' '}
                Last Name{' '}
                <span className="bulk-provision-guide__hint">(max 100)</span>,{' '}
                Title{' '}
                <span className="bulk-provision-guide__hint">(max 255)</span>,{' '}
                {isDriverIdAccount ? null : <>Email, </>}Role{' '}
                <span className="bulk-provision-guide__hint">
                  (valid options: executive, manager, staff, driver)
                </span>
              </span>
            </div>
            <div className="bulk-provision-guide__row">
              <span className="bulk-provision-guide__label">Optional</span>
              <span>
                Phone{' '}
                <span className="bulk-provision-guide__hint">
                  (US format, e.g. 555-123-4567)
                </span>
              </span>
            </div>
            <div className="bulk-provision-guide__row">
              <span className="bulk-provision-guide__label">Drivers only</span>
              <span>
                Driver ID{' '}
                <span className="bulk-provision-guide__hint">
                  ({isDriverIdAccount ? 'required' : 'optional'})
                </span>
                , Manager Email{' '}
                <span className="bulk-provision-guide__hint">(required)</span>
                {isDriverIdAccount && (
                  <>
                    , Email{' '}
                    <span className="bulk-provision-guide__hint">
                      (optional — this organization uses Driver ID login)
                    </span>
                  </>
                )}
              </span>
            </div>
          </div>

          <hr className="form-seperator"></hr>
          <Formik<FormValues>
            initialValues={{
              accountUuid: account || '',
              file: null,
              sendInvitation: false,
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              setFieldValue,
              setFieldTouched,
              values,
              isSubmitting,
              errors,
              touched,
            }) => (
              <Form>
                <div className="form-container">
                  <div className="form-row">
                    <div className="form-col">
                      <Label htmlFor="accountUuid">Account *</Label>
                      <SelectDropdown
                        id="accountUuid"
                        name="accountUuid"
                        value={values.accountUuid}
                        placeholder="Select an account"
                        invalid={!!(errors.accountUuid && touched.accountUuid)}
                        onChange={(e) => {
                          setFieldValue('accountUuid', e.target.value);
                          // The guide above lives outside Formik, so mirror the choice
                          // into state — the required fields differ per channel.
                          setSelectedAccountUuid(e.target.value);
                          // Clear the choice rather than merely hiding it: a box ticked
                          // before switching to a Driver ID account would otherwise stay
                          // true and still invite that file's managers.
                          setFieldValue('sendInvitation', false);
                        }}
                        onBlur={() => setFieldTouched('accountUuid', true)}
                      >
                        <option value="">Select Account</option>
                        {accounts.map((acct) => (
                          <option key={acct.uuid} value={acct.uuid}>
                            {acct.name}
                          </option>
                        ))}
                      </SelectDropdown>
                      {errors.accountUuid && touched.accountUuid && (
                        <p className="invalid-feedback">{errors.accountUuid}</p>
                      )}
                    </div>
                    <div className="form-col">
                      <Label htmlFor="csvFile"> CSV File *</Label>
                      <FileInput
                        id="csvFile"
                        acceptableFileFormats=".csv"
                        placeholder="Drag & drop your CSV here or click to browse"
                        value={values.file}
                        invalid={!!(errors.file && touched.file)}
                        onChange={(file) => {
                          setFieldValue('file', file);
                          if (!file) setFieldTouched('file', false, false);
                        }}
                      />
                      {errors.file && touched.file && (
                        <p className="invalid-feedback">{errors.file}</p>
                      )}
                    </div>
                  </div>

                  {/* Hidden on the Driver ID channel: drivers are handed their credentials
                      in person and the server already suppresses their invitation, so the
                      option only invites confusion. It does mean the managers and
                      executives in the same file are not invited either — accepted for
                      now, pending a manager-specific invitation email. */}
                  {!isDriverIdAccount && (
                    <div className="form-row">
                      <div className="form-col">
                        <div className="bulk-provision-checkbox-row">
                          <Input
                            type="checkbox"
                            id="sendInvitation"
                            checked={values.sendInvitation}
                            onChange={(e) =>
                              setFieldValue('sendInvitation', e.target.checked)
                            }
                          />
                          <Label htmlFor="sendInvitation" className="mb-0">
                            Send invitation to users after provisioning
                          </Label>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="button-container-right">
                    <Button
                      type="button"
                      variant="basic-secondary"
                      size="medium"
                      onClick={() =>
                        account
                          ? navigate({
                              to: '/accounts/$uuid',
                              params: { uuid: account },
                              search: { tab: 'overview' },
                            })
                          : navigate({ to: '/organizations' })
                      }
                      disabled={isUploading || isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="basic-primary"
                      size="medium"
                      disabled={isUploading || isSubmitting}
                    >
                      {isUploading && <Spinner size="small" />}
                      Upload
                    </Button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>

          {(parseErrors.length > 0 || result) && (
            <>
              <hr className="form-seperator"></hr>
              <p className="bulk-provision-section-title">Results</p>
              {parseErrors.length > 0 && (
                <div className="invalid-feedback">
                  <p className="mt-4 mb-2">
                    The following errors were found in your CSV file. No users
                    have been created. Please fix the issues below and upload
                    your file again.
                  </p>
                  <ul className="mt-1">
                    {parseErrors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
              {result && (
                <div className="bulk-provision-results">
                  <div className="bulk-provision-summary">
                    <div className="bulk-provision-badge bulk-provision-badge--success">
                      <CheckCircle size={16} />
                      <span>
                        <strong>{result.successCount}</strong> succeeded
                      </span>
                    </div>

                    {result.failureCount > 0 && (
                      <div className="bulk-provision-badge bulk-provision-badge--failure">
                        <XCircle size={16} />
                        <span>
                          <strong>{result.failureCount}</strong> failed
                        </span>
                      </div>
                    )}
                  </div>

                  {failures.length > 0 && (
                    <div className="invalid-feedback">
                      <Label>Failed Rows</Label>
                      {failures.map((failure: BulkProvisionResult) => (
                        <div key={failure.row}>
                          Row {failure.row}
                          {/* Drivers on the Driver ID channel have no email, so fall
                              back to the Driver ID rather than rendering empty parens. */}
                          {failure.email || failure.driverId
                            ? ` (${failure.email || failure.driverId})`
                            : ''}
                          : {failure.error}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </BaseLayout>
  );
}
